import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, Tag, X as XIcon } from "lucide-react";

/**
 * TagsSection — the tags card on the Result page. Owns nothing but
 * presentation: the parent still holds `scan.tags`, the known-tags
 * suggestion pool, the input draft, and the save-in-flight flag.
 *
 * Layout: heading + count, chip row of applied tags (each with a
 * remove button), chip row of suggested known tags the user hasn't
 * applied yet, and a text input + submit button. The suggestion row
 * and input hide once the user hits the 10-tag limit.
 */
export default function TagsSection({
  tags,
  knownTags,
  tagInput,
  onTagInputChange,
  savingTags,
  onSubmitAdd,
  onApplyKnown,
  onRemoveTag,
}) {
  const { t } = useTranslation();
  const scanLower = new Set((tags || []).map((x) => String(x).toLowerCase()));
  const availableSuggestions = knownTags
    .filter((tg) => !scanLower.has(String(tg).toLowerCase()))
    .slice(0, 12);
  const belowLimit = (tags || []).length < 10;

  return (
    <div className="mb-4 bg-white border border-[#E8E5DF] rounded-3xl p-4" data-testid="tags-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-[0.18em] text-leaf font-medium flex items-center gap-1.5">
          <Tag className="w-3.5 h-3.5" strokeWidth={1.5} />
          {t("result.tagsTitle")}
        </h3>
        <span className="text-[10px] uppercase tracking-[0.14em] text-leaf-mist">
          {t("result.tagsCount", { n: (tags || []).length, max: 10 })}
        </span>
      </div>

      {(tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="tags-list">
          {(tags || []).map((tg) => {
            const slug = tg.toLowerCase().replace(/[^a-z0-9]+/g, "-");
            return (
              <span
                key={tg}
                data-testid={`tag-${slug}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-parchment text-leaf-ink px-3 py-1 text-xs"
              >
                {tg}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveTag(tg); }}
                  aria-label={t("result.tagRemoveAria", { tag: tg })}
                  data-testid={`tag-remove-${slug}`}
                  className="text-leaf-mist hover:text-rust w-5 h-5 flex items-center justify-center rounded-full"
                >
                  <XIcon className="w-3 h-3 pointer-events-none" strokeWidth={1.5} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {belowLimit && (
        <>
          {availableSuggestions.length > 0 && (
            <div className="mb-2.5" data-testid="tag-suggestions">
              <p className="text-[10px] uppercase tracking-[0.14em] text-leaf-mist mb-1.5">
                {t("result.tagSuggestionsLabel")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {availableSuggestions.map((tg) => {
                  const slug = tg.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  return (
                    <button
                      key={tg}
                      type="button"
                      onClick={() => onApplyKnown(tg)}
                      disabled={savingTags}
                      data-testid={`tag-suggestion-${slug}`}
                      className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white text-leaf-ink border border-[#E8E5DF] hover:bg-parchment transition disabled:opacity-50"
                    >
                      <Plus className="w-3 h-3" strokeWidth={1.5} /> {tg}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <form onSubmit={onSubmitAdd} className="flex gap-2" data-testid="tag-add-form">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => onTagInputChange(e.target.value)}
              maxLength={30}
              placeholder={t("result.tagsPlaceholder")}
              data-testid="tag-input"
              aria-label={t("result.tagsTitle")}
              className="flex-1 bg-white border border-[#E8E5DF] rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-leaf/30"
            />
            <button
              type="submit"
              disabled={savingTags || !tagInput.trim()}
              data-testid="tag-add-button"
              className="rounded-full bg-leaf hover:bg-leaf-deep disabled:opacity-50 text-white text-xs px-4 flex items-center gap-1.5 transition"
            >
              {savingTags
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                : <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />}
              {t("result.tagAdd")}
            </button>
          </form>
        </>
      )}
      <p className="mt-2 text-[11px] text-leaf-mist">{t("result.tagsHelp")}</p>
    </div>
  );
}
